/**
 * Full name answers for the Name question type.
 *
 * A name is kept exactly as the respondent typed or said it: only boundary
 * whitespace is trimmed, internal spelling, spacing and casing are preserved,
 * and nothing is ever split into parts or truncated.
 *
 * @module lib/name-answers
 */
import type { NameConfig } from '../types';

/** Upper bound for a full name, in Unicode code points. */
export const MAX_NAME_CODE_POINTS = 500;

/** Versioned stored answer for a full-name question. */
export interface NameAnswer {
  version: 1;
  kind: 'name';
  format: 'full';
  fullName: string;
}

const ANSWER_KEYS = ['version', 'kind', 'format', 'fullName'];
const CONTROL_CHARACTER = /\p{Cc}/u;

/** Recognizes plain JSON object records, excluding arrays and null. */
function record(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/** Only the full format exists in this version; parts are a later, separately versioned format. */
export function isNameConfig(value: unknown): value is NameConfig {
  return record(value) && Object.keys(value).every(key => key === 'version' || key === 'format')
    && value.version === 1 && value.format === 'full';
}

/**
 * Normalizes a typed, spoken or stored full name into its versioned answer.
 * Returns null for empty, oversized or control-character input rather than altering it.
 */
export function normalizeFullName(value: unknown): NameAnswer | null {
  let raw: unknown = value;
  if (record(value)) {
    if (!Object.keys(value).every(key => ANSWER_KEYS.includes(key)) || value.version !== 1
      || value.kind !== 'name' || value.format !== 'full') return null;
    raw = value.fullName;
  }
  if (typeof raw !== 'string') return null;
  const fullName = raw.trim();
  if (!fullName || CONTROL_CHARACTER.test(fullName) || [...fullName].length > MAX_NAME_CODE_POINTS) return null;
  return { version: 1, kind: 'name', format: 'full', fullName };
}

/** Recognizes an already normalized stored answer, without accepting forged extra keys. */
export function isNameAnswer(value: unknown): value is NameAnswer {
  const normalized = normalizeFullName(value);
  return !!normalized && record(value) && value.fullName === normalized.fullName;
}

/** Display text for a stored full-name answer. */
export function formatNameAnswer(answer: NameAnswer): string {
  return answer.fullName;
}
